// File: src/pages/DepartmentRoleAssign.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Input, Label, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

// ✅ Your canonical paths
import {
  Get_RoleList,
  _GetList,
  _PostSave,
} from "../import/ApiEndPoint/ApiCalling";
import { List_Department, Save_Assigned_Role } from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("dept-role-inline-css")) return;
  const s = document.createElement("style");
  s.id = "dept-role-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .role-chip{ display:flex; align-items:center; gap:10px; border:1px solid #e5e7eb; border-radius:12px; padding:10px; }
    .role-chip:hover{ background:#f9fbff; }
    .rank-input{ width:100px; }
  `;
  document.head.appendChild(s);
};

export default function DepartmentRoleAssign() {
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]); // array of role objects (same refs as roles)
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => injectOnce(), []);

  const loadData = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");

      // Departments
      const url = `${List_Department}/${user?.database}`;
      const depRes = await _GetList(url);
      const activeDeps = (depRes?.Department || []).filter(
        (d) => d?.status === "Active"
      );
      setDepartments(activeDeps);

      // Roles (unassigned, not SuperAdmin)
      const roleRes = await Get_RoleList(user?._id, user?.database);
      const withoutAssign = (roleRes?.Role || []).filter(
        (r) => r?.assign === 0 && r?.roleName !== "SuperAdmin"
      );
      setRoles(withoutAssign);
    } catch (err) {
      console.error("[DepartmentRoleAssign] load error:", err);
      swal("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // search filter
  const filteredRoles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((r) =>
      (r?.roleName || "").toLowerCase().includes(term)
    );
  }, [roles, search]);

  const isSelected = (role) => selectedRoles.some((s) => s?._id === role?._id);

  const toggleRole = (role) => {
    setSelectedRoles((prev) => {
      const exists = prev.some((s) => s?._id === role?._id);
      if (exists) return prev.filter((s) => s?._id !== role?._id);
      return [...prev, role];
    });
  };

  const setRankFor = (roleId, rank) => {
    setSelectedRoles((prev) =>
      prev.map((r) =>
        r?._id === roleId ? { ...r, createdPosition: Number(rank) } : r
      )
    );
  };

  const clearSelection = () => setSelectedRoles([]);

  const assignRoles = async () => {
    if (!selectedDepartment) return swal("Please select a department");
    if (selectedRoles.length === 0)
      return swal("Please select at least one role");

    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const mainData = selectedRoles.map((r) => ({
      roleName: r?.roleName,
      database: r?.database,
      roleId: r?._id,
      rolePosition: r?.createdPosition, // optional rank
    }));

    const payload = {
      created_by: user?._id,
      database: user?.database,
      departmentName: selectedDepartment, // department _id
      roles: mainData,
    };

    try {
      setLoading(true);
      const res = await _PostSave(Save_Assigned_Role, payload);
      if (res) {
        swal("Roles Assigned Successfully");
        clearSelection();
        await loadData(); // refresh roles list (some may become assigned)
      }
    } catch (err) {
      console.error(err);
      swal("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const selectedCount = selectedRoles.length;

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Assign Roles to Department{" "}
                <span className="count-pill">{selectedCount}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search roles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Button
                outline
                color="secondary"
                style={{ height: 32 }}
                onClick={clearSelection}
                disabled={selectedCount === 0}
              >
                Clear
              </Button>

              {/* <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() =>
                  navigate("/app/freshlist/account/CreateHeirarchy")
                }
              >
                View Hierarchy
              </Button>
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate("/app/edit-team-role-position")}
              >
                Edit Hierarchy
              </Button> */}
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Selector Card ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 14, marginBottom: 12 }}>
            <Row style={{ gap: 12 }}>
              <Col lg="4" md="6" sm="12">
                <Label>Select Department</Label>
                <Input
                  type="select"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <option value="">-- Select Department --</option>
                  {departments.map((d) => (
                    <option key={d?._id} value={d?._id}>
                      {d?.departmentName}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col
                lg="8"
                md="6"
                sm="12"
                style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
              >
                <Button
                  color="primary"
                  onClick={assignRoles}
                  disabled={!selectedDepartment || selectedRoles.length === 0}
                >
                  Assign Roles
                </Button>
                <div style={{ color: "#6b7280" }}>
                  {selectedDepartment
                    ? ""
                    : "Select a department to enable assignment"}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ===== Roles Grid (3 columns) ===== */}
      <Row>
        {filteredRoles.length === 0 ? (
          <Col sm="12">
            <Card
              className="p-card"
              style={{ padding: 16, textAlign: "center" }}
            >
              No roles found.
            </Card>
          </Col>
        ) : (
          filteredRoles.map((role) => {
            const checked = isSelected(role);
            const rank =
              selectedRoles.find((s) => s?._id === role?._id)
                ?.createdPosition || "";
            return (
              <Col
                key={role?._id}
                lg="4"
                md="6"
                sm="12"
                style={{ marginBottom: 12 }}
              >
                <Card className="p-card" style={{ padding: 12 }}>
                  <label className="role-chip" htmlFor={`role_${role?._id}`}>
                    <Input
                      id={`role_${role?._id}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(role)}
                      style={{ width: 18, height: 18 }}
                    />
                    <div style={{ flex: 1, fontWeight: 700 }}>
                      {role?.roleName?.length > 22
                        ? `${role?.roleName.slice(0, 22)}…`
                        : role?.roleName}
                    </div>
                    <Input
                      className="rank-input"
                      type="number"
                      placeholder="Rank"
                      value={rank}
                      onChange={(e) => setRankFor(role?._id, e.target.value)}
                      disabled={!checked}
                    />
                  </label>
                </Card>
              </Col>
            );
          })
        )}
      </Row>
    </>
  );
}
